import { FC, memo, useCallback } from 'react'
import styled, { css } from 'styled-components'
import InfoBlock from './InfoBlock'
import PercentSelector from './PercentSelector'
import OrderInputErrors from './OrderInputErrors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectPricesConnectionError } from 'state/prices/selectors'
import Error from 'components/ErrorView'
import Spacer from 'components/Spacer'
import MarketsDropdown from './MarketsDropdown'
import { OrderDirection } from '@bitly/sdk/types'
import { selectOrderbook, selectOrderDirection, selectOrderType } from 'state/exchange/selectors'
import { setOrderDirection, setOrderType } from 'state/exchange/reducer'
import CloseOnlyPrompt from './CloseOnlyPrompt'
import PositionButtons from './PositionButtons'
import OrderTypeSelector from './OrderTypeSelector'
import AmountInput from './AmountInput'
import TotalInput from './TotalInput'
import TradePanelPriceInput from './TradePanelPriceInput'
import ManagePosition from './ManagePosition'
import SlippageSelector from './SlippageSelector'
import Liquid from './Liquid/Liquid'

type Props = {
	mobile?: boolean
	closeDrawer?: () => void
}

const TradePanelSmartMargin: FC<Props> = memo(({ mobile, closeDrawer }) => {
	const dispatch = useAppDispatch()

	const orderDirection = useAppSelector(selectOrderDirection)
	const orderType = useAppSelector(selectOrderType)
	const pricesConnectionError = useAppSelector(selectPricesConnectionError)

	const isLiquidSelected = orderDirection === ('liquid' as OrderDirection)

	const handleChangeSide = useCallback(
		(side: OrderDirection) => {
			dispatch(setOrderDirection(side))
		},
		[dispatch]
	)

	const renderTradingSection = useCallback(
		() => {
			return (
				<>
					{orderType !== 'market' && (
						<>
							<Spacer height={16} />
							<TradePanelPriceInput />
						</>
					)}

					<Spacer height={16} />
					<AmountInput />

					<Spacer height={16} />
					<TotalInput />

					<Spacer height={16} />
					<OrderInputErrors />

					<Spacer height={16} />
					<PercentSelector />

					{orderType === 'market' &&
						<>
							<Spacer height={16} />
							<SlippageSelector />
						</>
					}

					<Spacer height={16} />
					<InfoBlock />

					<Spacer height={16} />
					<ManagePosition />
				</>
			);
		},
		[orderType]
	)

	return (
		<TradePanelContainer $mobile={mobile}>
			{!mobile && (
				<>
					<MarketsDropdown />
					{/* <TradeBalance /> */}
				</>
			)}

			{process.env.NEXT_PUBLIC_CLOSE_ONLY === 'true' ? (
				<CloseOnlyPrompt $mobile={mobile} />
			) : (
				<>
					<PositionButtons
						selected={orderDirection}
						onSelect={handleChangeSide}
						mobile={mobile}
						closeDrawer={closeDrawer}
					/>

					<MainPanelContent $mobile={mobile}>
						{pricesConnectionError && (
							<Error message="Failed to connect to price feed. Please try disabling any ad blockers and refresh." />
						)}
						{isLiquidSelected ? (
							<Liquid mobile={mobile} />
						) : (
							<>
								<OrderTypeSelector orderType={orderType} setOrderTypeAction={setOrderType} />
								{renderTradingSection()}
							</>
						)}
					</MainPanelContent>
				</>
			)}
		</TradePanelContainer>
	)
})

const TradePanelContainer = styled.div<{ $mobile?: boolean }>`
	overflow-y: scroll;
	height: 100%;
	scrollbar-width: none;
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`

const MainPanelContent = styled.div<{ $mobile?: boolean }>`
	padding: 0 15px;

	${(props) =>
		props.$mobile &&
		css`
			padding: 65px 15px 0;
		`}
`

export default TradePanelSmartMargin
